import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "EtapaProductiva";

const normalizarTexto = (texto = "") =>
  texto
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();

const leerJSON = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const obtenerDataPermisos = () => {
  const permisosStorage = leerJSON("permisos", null);
  const usuarioStorage = leerJSON("usuario", null);
  const userStorage = leerJSON("user", null);
  const usuariosStorage = leerJSON("usuarios", null);

  return permisosStorage || usuarioStorage || userStorage || usuariosStorage || {};
};

const obtenerModulos = () => {
  const data = obtenerDataPermisos();

  if (Array.isArray(data)) return data;

  return (
    data?.modulos ||
    data?.permisos ||
    data?.rol?.modulos ||
    data?.rol?.permisos ||
    data?.usuario?.modulos ||
    data?.usuario?.permisos ||
    data?.user?.modulos ||
    data?.user?.permisos ||
    []
  );
};

const obtenerModulo = (nombreModulo) => {
  const modulos = obtenerModulos();

  return (
    modulos.find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.nombre ||
        modulo?.name ||
        "";

      return normalizarTexto(nombre) === normalizarTexto(nombreModulo);
    }) || {}
  );
};

const valorActivo = (valor) =>
  valor === true ||
  valor === 1 ||
  valor === "1" ||
  valor === "true" ||
  valor === "TRUE" ||
  valor === "si" ||
  valor === "SI" ||
  valor === "Sí" ||
  valor === "SÍ";

const tienePermiso = (modulo, accion) => {
  if (!modulo) return false;

  const accionUpper = accion.toUpperCase();
  const accionLower = accion.toLowerCase();

  const campos = [
    accionUpper,
    accionLower,
    `puede${accionUpper}`,
    `puede${accionLower}`,
    `puede${accionUpper.charAt(0)}${accionLower.slice(1)}`,
  ];

  const existeCampoActivo = campos.some((campo) => valorActivo(modulo?.[campo]));

  const existeEnArray =
    modulo?.acciones?.some(
      (a) => normalizarTexto(a) === normalizarTexto(accion)
    ) ||
    modulo?.permisos?.some(
      (a) => normalizarTexto(a) === normalizarTexto(accion)
    ) ||
    modulo?.operaciones?.some(
      (a) => normalizarTexto(a) === normalizarTexto(accion)
    );

  return existeCampoActivo || existeEnArray;
};

const ListarEProductiva = () => {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const moduloEtapaProductiva = useMemo(() => obtenerModulo(MODULO), []);

  const puedeCrear = tienePermiso(moduloEtapaProductiva, "CREAR");
  const puedeLeer = tienePermiso(moduloEtapaProductiva, "LEER");
  const puedeActualizar = tienePermiso(moduloEtapaProductiva, "ACTUALIZAR");
  const puedeEliminar = tienePermiso(moduloEtapaProductiva, "ELIMINAR");

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const toDate = useCallback((d) => {
    if (!d) return "—";

    const date = new Date(d);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleDateString("es-CO");
  }, []);

  const deleteEPractica = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar etapas productivas.",
          "warning"
        );
        return;
      }

      try {
        const res = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!res.isConfirmed) return;

        const { data } = await axios.delete(
          `/api/etapapractica/delete/${id}`,
          authConfig()
        );

        await Swal.fire({
          icon: "success",
          title: data?.message || "Eliminado correctamente",
          showConfirmButton: false,
          timer: 1500,
        });

        setItems((prev) => prev.filter((x) => x._id !== id));
      } catch (err) {
        console.error("Error al eliminar etapa:", err);

        if (manejarErrorSesion(err)) return;

        Swal.fire(
          "Error",
          err.response?.data?.message || "No se pudo eliminar la etapa.",
          "error"
        );
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  const ejecutarAccion = useCallback(
    async (accion, ep) => {
      if (!accion) return;

      const aprendizId = ep?.aprendizObjectId?._id;
      const matriculaId = ep?.matriculaObjectId?._id;

      switch (accion) {
        case "bitacoras":
          navigate(`/etapaproductiva/bitacoras/${ep._id}/${aprendizId}/${matriculaId}`);
          break;

        case "seguimiento":
          navigate(`/etapaproductiva/seguimiento/${ep._id}/${aprendizId}/${matriculaId}`);
          break;

        case "editar":
          if (!puedeActualizar) {
            Swal.fire(
              "Sin permisos",
              "No tiene permisos para actualizar etapas productivas.",
              "warning"
            );
            return;
          }

          navigate(`/etapaproductiva/editar/${ep._id}`);
          break;

        case "eliminar":
          if (!puedeEliminar) {
            Swal.fire(
              "Sin permisos",
              "No tiene permisos para eliminar etapas productivas.",
              "warning"
            );
            return;
          }

          await deleteEPractica(ep._id);
          break;

        default:
          break;
      }
    },
    [navigate, puedeActualizar, puedeEliminar, deleteEPractica]
  );

  const renderAcciones = useCallback(
    (ep) => (
      <div className="d-flex justify-content-end">
        <div className="input-group input-group-sm" style={{ width: 270 }}>
          <span className="input-group-text bg-white">
            <i className="fa-solid fa-gear"></i>
          </span>

          <select
            className="form-select"
            defaultValue=""
            onChange={(e) => {
              const accion = e.target.value;
              e.target.value = "";
              ejecutarAccion(accion, ep);
            }}
          >
            <option value="" disabled>
              Acciones de etapa…
            </option>

            <optgroup label="Seguimiento del Aprendiz">
              <option value="bitacoras">📚 Bitácoras</option>
              <option value="seguimiento">📝 Seguimiento</option>
            </optgroup>

            <optgroup label="Administración">
              {puedeActualizar && <option value="editar">✏️ Editar Etapa</option>}
              {puedeEliminar && <option value="eliminar">🗑️ Eliminar Etapa</option>}
            </optgroup>
          </select>
        </div>
      </div>
    ),
    [ejecutarAccion, puedeActualizar, puedeEliminar]
  );

  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setItems([]);
      return;
    }

    setCargando(true);

    try {
      const { data } = await axios.get(
        "/api/etapapractica/listall",
        authConfig()
      );

      const etapasConFunciones = (data?.etapas || []).map((ep) => {
        const nombreAprendiz = [
          ep?.aprendizObjectId?.nombre1Aprendiz,
          ep?.aprendizObjectId?.apellido1Aprendiz,
          ep?.aprendizObjectId?.apellido2Aprendiz,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        const nombreInstructor = [
          ep?.instructorObjectId?.nombre1Instructor,
          ep?.instructorObjectId?.nombre2Instructor,
          ep?.instructorObjectId?.apellido1Instructor,
          ep?.instructorObjectId?.apellido2Instructor,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        const nombreJefe = [
          ep?.jefeObjectId?.nombre1Jefe,
          ep?.jefeObjectId?.apellido1Jefe,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        return {
          ...ep,
          documentoAprendiz: ep?.aprendizObjectId?.documentoAprendiz ?? "—",
          nombreAprendiz: nombreAprendiz || "—",
          codMatricula: ep?.matriculaObjectId?.codMatricula ?? "—",
          ente: ep?.enteObjectId?.nombreEnteconformador ?? "—",
          instructor: nombreInstructor || "—",
          jefe: nombreJefe || "—",
          fechaInicio: toDate(ep?.fechaInicioEtapaPractica),
          fechaFin: toDate(ep?.fechaFinEtapaPractica),
          funciones: renderAcciones(ep),
        };
      });

      setItems(etapasConFunciones);
    } catch (error) {
      console.error("Error al cargar las etapas productivas:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudieron cargar las etapas productivas.",
        "error"
      );

      setItems([]);
    } finally {
      setCargando(false);
    }
  }, [puedeLeer, manejarErrorSesion, toDate, renderAcciones]);

  useEffect(() => {
    getData();
  }, [getData]);

  const columns = useMemo(
    () => [
      { Header: "Documento", accessor: "documentoAprendiz" },
      {
        Header: "Aprendiz",
        accessor: "nombreAprendiz",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "Matrícula", accessor: "codMatricula" },
      { Header: "Alternativa", accessor: "alternativaEtapaPractica" },
      { Header: "Ente", accessor: "ente" },
      { Header: "Instructor", accessor: "instructor" },
      { Header: "Jefe", accessor: "jefe" },
      { Header: "Inicio", accessor: "fechaInicio" },
      { Header: "Fin", accessor: "fechaFin" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4 py-4">
        <div className="bg-white border rounded-4 shadow-sm p-4 text-center">
          <div className="display-6 mb-3">
            <i className="fa-solid fa-lock"></i>
          </div>

          <h5 className="fw-semibold">Sin permiso de lectura</h5>

          <p className="text-secondary">
            No tienes permisos para visualizar etapas productivas.
          </p>

          <button
            className="btn btn-outline-dark"
            type="button"
            onClick={() => navigate(-1)}
          >
            <i className="fa-solid fa-arrow-left me-2"></i>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Listado de etapas productivas
                </h5>

                <small className="text-secondary">
                  Seguimiento y control de la etapa práctica del aprendiz.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">

                 <button
                    className="btn btn-outline-dark flex-fill"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  title="Volver al inicio"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>


                <button
                   className="btn btn-outline-secondary flex-fill"
                  type="button"
                  onClick={getData}
                  disabled={cargando}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${
                      cargando ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                      className="btn btn-dark flex-fill"
                    type="button"
                    onClick={() => navigate("/etapaproductiva/nuevo")}
                    title="Registrar etapa productiva"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            {cargando ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" />
                <div className="mt-3">Cargando etapas productivas…</div>
              </div>
            ) : items.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={items} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🧩</div>

                <div className="fw-semibold">No hay registros</div>

                <div className="small mb-3">
                  Crea el primer registro para iniciar el control.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate("/etapaproductiva/nuevo")}
                    title="Registrar etapa productiva"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar etapa
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarEProductiva;
